import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import AddMemberForm from "./AddMemberForm";
import EditMemberForm from "./EditMemberForm";
import {BackButton} from "../../components/BackButton";
import { MdOutlineKeyboardDoubleArrowUp, MdOutlineKeyboardDoubleArrowDown } from "react-icons/md";

import "./Member.css";
import { useHouseState, useHouseDispatch } from "../../contexts/HouseContext";

const SortIcon = ({ sortOrder }) => {
    return sortOrder === 'asc' ? <MdOutlineKeyboardDoubleArrowUp /> : <MdOutlineKeyboardDoubleArrowDown />;
};

const SORT_BY = {
    NAME: 'name',
    GAMES_PLAYED: 'gamesPlayed',
    HIGHEST_WIN: 'highestWin',
    BIGGEST_LOSS: 'biggestLoss',
};

const SORT_ORDER = {
    ASC: 'asc',
    DESC: 'desc',
};

export default function Members() {
    const { house, games } = useHouseState();
    const { loadGames } = useHouseDispatch();
    const [sortConfig, setSortConfig] = useState({ sortBy: SORT_BY.NAME, sortOrder: SORT_ORDER.ASC });

    useEffect(() => {
        const fetchGames = async () => {
            const gameIds = Object.entries(games).map(([id, ]) => id);
            await loadGames(gameIds);
        };
        fetchGames();
    }, []);

    if (!house) {
        return <div>Loading...</div>
    }

    const handleSort = (sortBy) => {
        setSortConfig(prevConfig => ({
            sortBy,
            sortOrder: (prevConfig.sortBy === sortBy && prevConfig.sortOrder === SORT_ORDER.DESC) 
                || (prevConfig.sortBy !== sortBy && sortBy === SORT_BY.NAME)
                    ? SORT_ORDER.ASC 
                    : SORT_ORDER.DESC
        }));
    };

    const calculateMemberStats = (memberId) => {
        let gamesPlayed = 0;
        let highestWin = 0;
        let biggestLoss = 0;

        Object.values(games).forEach(game => {
            const player = game.players?.find(p => p.memberId === memberId);
            if (player) {
                gamesPlayed++;
                const net = player.totals?.net || 0;
                if (net > 0 && net > highestWin) {
                    highestWin = net;
                }
                if (net < 0 && net < biggestLoss) {
                    biggestLoss = net;
                }
            }
        });

        return { gamesPlayed, highestWin, biggestLoss };
    };

    const calculatedMemberStats = house.members.reduce((acc, member) => (
            { ...acc, [member.id]: calculateMemberStats(member.id) }
    ), {});

    const sortedMembers = [...house.members].sort((a, b) => {
        const aStats = calculatedMemberStats[a.id];
        const bStats = calculatedMemberStats[b.id];
        
        const multiplier = sortConfig.sortOrder === 'asc' ? 1 : -1;
        
        switch (sortConfig.sortBy) {
            case SORT_BY.NAME:
                return a.name.localeCompare(b.name) * multiplier;
            case SORT_BY.GAMES_PLAYED:
                return (aStats.gamesPlayed - bStats.gamesPlayed) * multiplier;
            case SORT_BY.HIGHEST_WIN:
                return (aStats.highestWin - bStats.highestWin) * multiplier;
            case SORT_BY.BIGGEST_LOSS:
                return (Math.abs(aStats.biggestLoss) - Math.abs(bStats.biggestLoss)) * multiplier;
            default:
                return 0;
        }
    });

    const memberCards = sortedMembers.map((m, i) => (
        <Col xs={6} md={3} key={i} className="mb-3">
            <MemberCard member={m} />
        </Col>
    ));

    return (
        <div>
            <Row className="d-flex align-items-center mb-3">
                <Col xs={1}>
                    <BackButton/>
                </Col>
                <Col xs={9} md={4} className="mx-auto">
                    <h2>House Members</h2>
                </Col>
                <Col xs={12} md={6} >
                    <AddMemberForm/>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="d-flex justify-content-end gap-3">
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => handleSort(SORT_BY.NAME)}
                            className={`sort-button ${sortConfig.sortBy === SORT_BY.NAME ? 'active' : ''}`}
                        >
                            Name {sortConfig.sortBy === SORT_BY.NAME && <SortIcon sortOrder={sortConfig.sortOrder} />}
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => handleSort(SORT_BY.GAMES_PLAYED)}
                            className={`sort-button ${sortConfig.sortBy === SORT_BY.GAMES_PLAYED ? 'active' : ''}`}
                        >
                            Games {sortConfig.sortBy === SORT_BY.GAMES_PLAYED && <SortIcon sortOrder={sortConfig.sortOrder} />}
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => handleSort(SORT_BY.HIGHEST_WIN)}
                            className={`sort-button ${sortConfig.sortBy === SORT_BY.HIGHEST_WIN ? 'active' : ''}`}
                        >
                            Highest Win {sortConfig.sortBy === SORT_BY.HIGHEST_WIN && <SortIcon sortOrder={sortConfig.sortOrder} />}
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => handleSort(SORT_BY.BIGGEST_LOSS)}
                            className={`sort-button ${sortConfig.sortBy === SORT_BY.BIGGEST_LOSS ? 'active' : ''}`}
                        >
                            Biggest Loss {sortConfig.sortBy === SORT_BY.BIGGEST_LOSS && <SortIcon sortOrder={sortConfig.sortOrder} />}
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                {memberCards}
            </Row>
        </div>
    );
};

const MemberCard = ({ member }) => {
    const [editing, setEditing] = useState(false)
    const [stats, setStats] = useState({
        gamesPlayed: 0,
        highestWin: 0,
        biggestLoss: 0
    });

    const { updateMember } = useHouseDispatch();
    const { games } = useHouseState();

    useEffect(() => {
        // Calculate member stats from house games
        const calculateStats = () => {
            let gamesPlayed = 0;
            let highestWin = 0;
            let biggestLoss = 0;

            Object.values(games).forEach(game => {
                const player = game.players?.find(p => p.memberId === member.id);
                if (player) {
                    gamesPlayed++;
                    const net = player.totals?.net || 0;
                    if (net > 0 && net > highestWin) {
                        highestWin = net;
                    }
                    if (net < 0 && net < biggestLoss) {
                        biggestLoss = net;
                    }
                }
            });

            setStats({ gamesPlayed, highestWin, biggestLoss });
        };

        calculateStats();
    }, [games, member.id]);

    const enableEditing = () => setEditing(true)
    const submitEdit = async (updatedName) => {
        await updateMember({ id: member.id, name: updatedName });
        setEditing(false);
    }

    const memberContent = editing
        ? <EditMemberForm member={member} submitEdit={submitEdit}></EditMemberForm>
        : <>
            <div className="member-header">
                <h5 className="mb-1">{member.name}</h5>
                <small className="text-muted">{stats.gamesPlayed} games</small>
            </div>
            <div className="member-stats">
                <MemberStatItem label='Highest Win' value={stats.highestWin} />
                <MemberStatItem label='Biggest Loss' value={stats.biggestLoss} />
            </div>
            <div className="member-actions">
                <Button type='button' onClick={enableEditing} size="sm">Edit</Button>
            </div>
        </>;

    return (
        <Card className={"member-card"}>
            <Card.Body className="py-2">
                {memberContent}
            </Card.Body>
        </Card>
    );
}

const MemberStatItem = ({ label, value }) => {
    const style = !value ? 'text-muted' : (value > 0 ? 'text-success' : 'text-danger');
    return (
        <div className="stat-item">
            <div className="stat-label">{label}</div>
            <div className={`stat-value ${style}`}>
                {Math.abs(value) > 0 ? `$${Math.abs(value).toFixed(2)}` : 'N/A'}
            </div>
        </div>
    );
};

